import { NextPageParams, type Note, type NoteVersionResponse, type NoteVersionsResponse } from "@/schema"
import { useInfiniteQuery, useQuery } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import {
  fetchBusinessNotes,
  fetchContactNotes,
  fetchHouseholdNotes,
  fetchNote,
  fetchNoteVersion,
  fetchNoteVersions,
  fetchPersonNotes,
  fetchTrustNotes,
  NotesResponse,
} from "./api"

interface InfiniteNotesData {
  notes: Note[]
  totalCount: number
}

export function useFetchNoteVersion(noteId: string, versionId?: string) {
  const getContext = useApiContext()

  return useQuery<NoteVersionResponse>({
    queryKey: ["note-version", noteId, versionId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      if (!versionId) throw new Error("No version found")
      return fetchNoteVersion(context, noteId, versionId)
    },
    enabled: Boolean(noteId && versionId),
  })
}

export function useInfiniteNoteVersions(noteId: string) {
  const getContext = useApiContext()

  return useInfiniteQuery<
    NoteVersionsResponse,
    Error,
    {
      current: NoteVersionsResponse["current"] | undefined
      versions: NoteVersionsResponse["versions"]
      totalCount: number
    },
    [string, string],
    NextPageParams | undefined
  >({
    queryKey: ["note-versions", noteId],
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchNoteVersions(context, noteId, pageParam)
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: (data) => ({
      current: data.pages[0]?.current,
      versions: data.pages.flatMap((page) => page.versions),
      totalCount: (data.pages[0]?.pagination.total_count ?? 0) + 1,
    }),
    enabled: Boolean(noteId),
  })
}

function selectInfiniteNotes(data: { pages: NotesResponse[] }): InfiniteNotesData {
  return {
    notes: data.pages.flatMap((page) => page.notes),
    totalCount: data.pages[0]?.pagination.total_count ?? 0,
  }
}

export function useFetchNote(id: string) {
  const getContext = useApiContext()

  return useQuery<Note>({
    queryKey: ["note", id],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return fetchNote(context, id)
    },
    enabled: !!id,
  })
}

export function useInfiniteHouseholdNotes(
  householdId: string,
  search?: string,
  showHiddenNotes?: boolean,
  tagIds?: string[]
) {
  const getContext = useApiContext()

  return useInfiniteQuery<
    NotesResponse,
    Error,
    InfiniteNotesData,
    (string | string[] | undefined)[],
    NextPageParams | undefined
  >({
    queryKey: ["notes", "household", householdId, "infinite", search, showHiddenNotes ? "true" : "false", tagIds],
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchHouseholdNotes(context, householdId, search, pageParam, showHiddenNotes, tagIds)
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: selectInfiniteNotes,
    enabled: !!householdId,
  })
}

export function useInfiniteContactNotes(
  contactId: string,
  search?: string,
  showHiddenNotes?: boolean,
  tagIds?: string[]
) {
  const getContext = useApiContext()

  return useInfiniteQuery<
    NotesResponse,
    Error,
    InfiniteNotesData,
    (string | string[] | undefined)[],
    NextPageParams | undefined
  >({
    queryKey: ["notes", "contact", contactId, "infinite", search, showHiddenNotes ? "true" : "false", tagIds],
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchContactNotes(context, contactId, search, pageParam, showHiddenNotes, tagIds)
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: selectInfiniteNotes,
    enabled: !!contactId,
  })
}

export function useInfiniteBusinessNotes(
  businessId: string,
  search?: string,
  showHiddenNotes?: boolean,
  tagIds?: string[]
) {
  const getContext = useApiContext()

  return useInfiniteQuery<
    NotesResponse,
    Error,
    InfiniteNotesData,
    (string | string[] | undefined)[],
    NextPageParams | undefined
  >({
    queryKey: ["notes", "business", businessId, "infinite", search, showHiddenNotes ? "true" : "false", tagIds],
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchBusinessNotes(context, businessId, search, pageParam, showHiddenNotes, tagIds)
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: selectInfiniteNotes,
    enabled: !!businessId,
  })
}

export function useInfiniteTrustNotes(trustId: string, search?: string, showHiddenNotes?: boolean, tagIds?: string[]) {
  const getContext = useApiContext()

  return useInfiniteQuery<
    NotesResponse,
    Error,
    InfiniteNotesData,
    (string | string[] | undefined)[],
    NextPageParams | undefined
  >({
    queryKey: ["notes", "trust", trustId, "infinite", search, showHiddenNotes ? "true" : "false", tagIds],
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchTrustNotes(context, trustId, search, pageParam, showHiddenNotes, tagIds)
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: selectInfiniteNotes,
    enabled: !!trustId,
  })
}

export function useInfinitePersonNotes(
  personId: string,
  search?: string,
  showHiddenNotes?: boolean,
  tagIds?: string[]
) {
  const getContext = useApiContext()

  return useInfiniteQuery<
    NotesResponse,
    Error,
    InfiniteNotesData,
    (string | string[] | undefined)[],
    NextPageParams | undefined
  >({
    queryKey: ["notes", "person", personId, "infinite", search, showHiddenNotes ? "true" : "false", tagIds],
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchPersonNotes(context, personId, search, pageParam, showHiddenNotes, tagIds)
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: selectInfiniteNotes,
    enabled: !!personId,
  })
}
